<app-modal
  [isVisible]="isVisible()"
  [config]="modalConfig()"
  [primaryButtonDisabled]="modalConfig().primaryButtonDisabled ?? false"
  [primaryButtonLoading]="isCreating()"
  [warnBeforeClose]="hasUnsavedWork() && !isCreating()"
  [discardDescription]="
    creationNeedsRecovery() ? ('modals.create_assessment.discard_unconfirmed' | transloco) : null
  "
  (confirmed)="onConfirmed()"
  (cancelled)="onDismiss()"
  (closed)="onDismiss()"
  (secondary)="onSecondary()"
  (tertiary)="onTertiary()"
>
  @if (isCreating()) {
    <app-modal-status
      variant="loading"
      [title]="'modals.create_assessment.creating_status' | transloco"
      [description]="'modals.create_assessment.creating_description' | transloco"
    >
      <app-huxley [state]="'thinking'" [size]="72" />
    </app-modal-status>
  } @else if (successMessage(); as message) {
    <app-modal-status
      variant="success"
      [title]="message"
      [description]="
        successOpenUrl() ? ('modals.create_assessment.success_description' | transloco) : null
      "
      [linkHref]="successOpenUrl()"
      [linkLabel]="'modals.create_assessment.success_open_link' | transloco"
    >
      <app-huxley [state]="'success'" [size]="72" />
    </app-modal-status>
  } @else {
    <!-- Wizard steps -->
    <app-step-indicator
      [steps]="stepConfigs()"
      [currentStep]="currentStep()"
      [maxReachedStep]="maxReachedStep()"
      [clickable]="true"
      (stepClicked)="onStepClicked($event)"
    ></app-step-indicator>

    <div class="create-modal-content">
      @if (createFailed(); as failure) {
        <app-modal-status
          variant="error"
          [title]="
            (creationNeedsRecovery()
              ? 'modals.create_assessment.error_title_unconfirmed'
              : 'modals.create_assessment.error_title'
            ) | transloco
          "
          [description]="failure"
        >
          <app-huxley [state]="'error'" [size]="72" />
        </app-modal-status>
      }

      @if (bannerMessage(); as banner) {
        <app-access-banner
          [fullWidth]="true"
          [notice]="{
            message: banner,
            icon: bannerIsDenial() ? 'lock' : 'warning',
            tone: bannerIsDenial() ? 'neutral' : 'warning',
          }"
        >
          @if (visibleErrorMessage() && errorLink(); as link) {
            <a
              class="modal-error__link"
              [href]="link.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ link.labelKey | transloco }}
            </a>
          }
        </app-access-banner>
      }

      @if (
        isLinkedToPa() &&
        (currentStep() === 'link' || (paLinkRequired() && currentStep() === 'review'))
      ) {
        @if (activityLoadFailed() || activityCheck() === 'error') {
          <app-access-banner
            [fullWidth]="true"
            [notice]="{
              message: ('modals.create_assessment.link_check_failed' | transloco),
              icon: 'warning',
              tone: 'warning',
            }"
          >
            <app-button type="secondary" size="small" (clicked)="retryActivityAvailability()">{{
              'modals.create_assessment.link_retry' | transloco
            }}</app-button>
          </app-access-banner>
        } @else if (isLoadingActivities() || activityCheck() === 'loading') {
          <p role="status" class="modal-intro muted">
            {{ 'modals.create_assessment.link_checking' | transloco }}
          </p>
        } @else if (currentStep() === 'link' && existingDpiaRows().length > 0) {
          <app-access-banner
            [fullWidth]="true"
            [notice]="{
              message: ('modals.create_assessment.link_existing_intro' | transloco),
              icon: 'info',
              tone: 'info',
            }"
          >
            <app-refresh-button
              noticeActions
              class="cam-existing-refresh"
              [loading]="isLoadingActivities()"
              (refreshClick)="refreshActivities()"
            />
            <ul class="cam-existing-assessments">
              @for (row of existingDpiaRows(); track row.id) {
                <li class="cam-existing-assessment">
                  <a
                    class="cam-existing-assessment__title"
                    [href]="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    [attr.aria-label]="
                      'modals.create_assessment.link_open_named' | transloco: { name: row.name }
                    "
                    [attr.title]="
                      'modals.create_assessment.link_open_named' | transloco: { name: row.name }
                    "
                  >
                    <span>{{ row.name }}</span>
                    <mat-icon aria-hidden="true">open_in_new</mat-icon>
                  </a>
                  <div class="cam-existing-assessment__meta">
                    <span>{{ row.activityName }}</span>
                    <app-status-indicator
                      appearance="inline"
                      size="small"
                      [status]="row.display.variant"
                      [meaning]="row.display.meaning"
                      [label]="row.display.labelKey | transloco"
                    />
                  </div>
                </li>
              }
            </ul>
          </app-access-banner>
        }
      }

      <!-- Step 0: Assessment-type picker (VER-284) — shown only when
           the org has more than one type entitled. Single-type orgs
           skip straight to Details so the historical UX is untouched. -->
      @if (currentStep() === 'type') {
        <p class="modal-intro">
          {{ 'modals.create_assessment.type_intro' | transloco }}
        </p>
        <div
          class="cam-type-picker"
          role="radiogroup"
          [attr.aria-label]="'modals.create_assessment.step_type' | transloco"
        >
          @for (entry of registeredTypes; track entry.type) {
            @let entitled = isTypeEntitled(entry.plugin);
            <button
              type="button"
              role="radio"
              class="cam-type-card"
              [class.cam-type-card--selected]="selectedType() === entry.type"
              [class.cam-type-card--disabled]="!entitled"
              [disabled]="!entitled"
              [attr.aria-checked]="selectedType() === entry.type"
              [attr.aria-disabled]="!entitled"
              [attr.title]="
                entitled ? null : ('modals.create_assessment.type_not_entitled' | transloco)
              "
              (click)="selectType(entry.type)"
            >
              <app-type-icon
                class="cam-type-card__icon"
                [icon]="entry.plugin.icon.icon"
                [variant]="entry.plugin.icon.variant"
              />
              <div class="cam-type-card__body">
                <span class="cam-type-card__title">{{ entry.plugin.labelKey | transloco }}</span>
                <span class="cam-type-card__description">
                  {{ entry.plugin.descriptionKey | transloco }}
                </span>
                @if (!entitled) {
                  <span class="cam-type-card__lock-hint">
                    <mat-icon aria-hidden="true">lock</mat-icon>
                    {{ 'modals.create_assessment.type_not_entitled' | transloco }}
                  </span>
                }
              </div>
              <span class="cam-type-card__check" aria-hidden="true">
                @if (selectedType() === entry.type) {
                  <mat-icon>check_circle</mat-icon>
                } @else {
                  <mat-icon>radio_button_unchecked</mat-icon>
                }
              </span>
            </button>
          }
        </div>
      }

      <!-- Step: Vendor picker (VER-331 step 2) — vendor-shaped types only
           (TIA today). Picking a vendor runs the read-only gate-preview
           check immediately; the wizard can only proceed on a "proceed"
           outcome. Once picked, the list collapses to a compact summary
           card with a "Change vendor" action. -->
      @if (currentStep() === 'vendor') {
        <p class="modal-intro">{{ 'tia.wizard.vendor_intro' | transloco }}</p>

        @if (selectedVendor(); as vendor) {
          <div class="cam-vendor-card">
            <app-org-logo
              [logoUrl]="logoUrlForVendor(vendor)"
              [name]="vendor.global_vendor?.name ?? ''"
              size="md"
            />
            <div class="cam-vendor-card__body">
              <span class="cam-vendor-card__name">{{ vendor.global_vendor?.name }}</span>
              @if (vendor.global_vendor?.headquarters_country; as hq) {
                <span class="cam-vendor-card__meta">{{ countryName(hq | uppercase) }}</span>
              }
            </div>
            <button type="button" class="cam-vendor-card__change" (click)="changeVendor()">
              {{ 'tia.wizard.change_vendor' | transloco }}
            </button>
          </div>

          @if (isLoadingGatePreview()) {
            <p class="modal-intro muted">{{ 'tia.wizard.gate_preview_loading' | transloco }}</p>
          } @else if (gatePreviewError()) {
            <div class="cam-gate-outcome cam-gate-outcome--error">{{ gatePreviewError() }}</div>
            @if (gatePreviewNeedsManualCountry()) {
              <div class="cam-manual-country">
                <app-select
                  variant="form"
                  [placeholder]="'tia.wizard.manual_country_placeholder' | transloco"
                  [options]="manualCountryOptions()"
                  [searchable]="true"
                  [value]="manualCountryCode()"
                  (valueChange)="manualCountryCode.set($any($event))"
                />
                <button
                  type="button"
                  class="cam-vendor-card__change"
                  [disabled]="!manualCountryCode()"
                  (click)="retryGatePreviewWithManualCountry()"
                >
                  {{ 'tia.wizard.manual_country_continue' | transloco }}
                </button>
              </div>
            }
          } @else if (gatePreview(); as preview) {
            @switch (preview.outcome) {
              @case ('adequate') {
                <div class="cam-gate-outcome cam-gate-outcome--adequate">
                  <mat-icon aria-hidden="true">check_circle</mat-icon>
                  <span>{{ 'tia.wizard.gate_outcome_adequate' | transloco }}</span>
                </div>
                <!-- VER-377 "Assess anyway" — a voluntary full TIA for an
                     adequacy-decision destination (never EEA). Blocked
                     until the country has a published risk assessment. -->
                @if (preview.assess_anyway_allowed) {
                  @if (preview.country_published) {
                    <ul class="context-docs-list cam-assess-anyway">
                      <li
                        class="context-doc-item"
                        [class.context-doc-item--selected]="assessAnyway()"
                        role="checkbox"
                        tabindex="0"
                        [attr.aria-checked]="assessAnyway()"
                        (click)="toggleAssessAnyway()"
                        (keydown.enter)="toggleAssessAnyway()"
                        (keydown.space)="$event.preventDefault(); toggleAssessAnyway()"
                      >
                        <mat-checkbox
                          color="primary"
                          class="ctx-row-check"
                          [checked]="assessAnyway()"
                          [disableRipple]="true"
                          tabindex="-1"
                          aria-hidden="true"
                          (click)="$event.preventDefault()"
                        />
                        <div class="context-doc-info">
                          <span class="context-doc-name">{{
                            'tia.wizard.assess_anyway_label' | transloco
                          }}</span>
                          <span class="context-doc-desc">{{
                            'tia.wizard.assess_anyway_desc' | transloco
                          }}</span>
                        </div>
                      </li>
                    </ul>
                  } @else {
                    <p class="tia-wizard-note">
                      {{ 'tia.wizard.assess_anyway_unavailable_pending' | transloco }}
                    </p>
                  }
                }
              }
              @case ('pending') {
                <div class="cam-gate-outcome cam-gate-outcome--pending">
                  <mat-icon aria-hidden="true">hourglass_top</mat-icon>
                  <span>{{ 'tia.wizard.gate_outcome_pending' | transloco }}</span>
                </div>
              }
              @case ('proceed') {
                <div class="cam-gate-outcome cam-gate-outcome--proceed">
                  <mat-icon aria-hidden="true">info</mat-icon>
                  <span>{{ 'tia.wizard.gate_outcome_proceed' | transloco }}</span>
                </div>
                <!-- Pre-population preview per the ticket: known country,
                     role, legal basis, safeguards from the registry. -->
                <div class="cam-review-row">
                  <span class="review-label">{{ 'tia.wizard.field_country' | transloco }}</span>
                  <span class="review-value">{{ countryName(preview.country_code) }}</span>
                </div>
                @if (preview.vendor_use?.privacy_role; as role) {
                  <div class="cam-review-row">
                    <span class="review-label">{{ 'tia.wizard.field_role' | transloco }}</span>
                    <span class="review-value">{{ privacyRoleLabel(role) }}</span>
                  </div>
                }
                @if (preview.vendor_use?.transfer_mechanism; as mechanism) {
                  <div class="cam-review-row">
                    <span class="review-label">{{
                      'tia.wizard.field_transfer_mechanism' | transloco
                    }}</span>
                    <span class="review-value">{{ transferMechanismLabel(mechanism) }}</span>
                  </div>
                  <!-- VER-381 — what the mechanism means, in plain language. -->
                  @if (transferMechanismExplanation(mechanism); as explanation) {
                    <p class="tia-wizard-note cam-mechanism-note">{{ explanation }}</p>
                  }
                  <!-- "No transfer" is also the default when nobody set the
                       field — flag it when the destination needs a TIA. -->
                  @if (mechanism === 'NOT_APPLICABLE_NO_TRANSFER') {
                    <div class="warn-banner" role="note">
                      <span>{{
                        'tia.wizard.no_transfer_declared_hint'
                          | transloco: { country: countryName(preview.country_code) }
                      }}</span>
                    </div>
                  }
                }
              }
            }
          }
        } @else if (isLoadingVendors()) {
          <p class="modal-intro muted">{{ 'tia.wizard.vendor_loading' | transloco }}</p>
        } @else if (vendorList().length === 0) {
          <p class="modal-intro muted">{{ 'tia.wizard.vendor_empty' | transloco }}</p>
        } @else {
          <ul
            class="cam-vendor-list"
            role="radiogroup"
            [attr.aria-label]="'tia.wizard.step_vendor' | transloco"
          >
            @for (vendor of vendorList(); track vendor.vendor_id) {
              <li>
                <button
                  type="button"
                  role="radio"
                  class="cam-vendor-row"
                  [attr.aria-checked]="selectedVendorId() === vendor.vendor_id"
                  (click)="selectVendor(vendor.vendor_id)"
                >
                  <app-org-logo
                    [logoUrl]="logoUrlForVendor(vendor)"
                    [name]="vendor.global_vendor?.name ?? ''"
                    size="sm"
                  />
                  <span class="cam-vendor-row__name">{{ vendor.global_vendor?.name }}</span>
                  @if (vendor.global_vendor?.headquarters_country; as hq) {
                    <span class="cam-vendor-row__meta">{{ hq | uppercase }}</span>
                  }
                </button>
              </li>
            }
          </ul>
        }
      }

      <!-- Step: PA linkage (FE-G / VER-284) — mandatory for DPIA-shaped
           assessment types, optional for vendor-shaped types (TIA).
           Users pick "link to an existing Processing Activity" or "start
           standalone" (materialise a PA later from the overview overflow
           menu / not at all for TIA). -->
      @if (currentStep() === 'link') {
        <p class="modal-intro">
          {{ 'modals.create_assessment.link_intro' | transloco }}
        </p>
        <div
          class="cam-link-picker"
          role="radiogroup"
          [attr.aria-label]="'modals.create_assessment.step_link' | transloco"
        >
          <label class="cam-link-radio" [class.selected]="isLinkedToPa()">
            <input
              type="radio"
              name="cam-link-mode"
              [checked]="isLinkedToPa()"
              (change)="onLinkModeChange(true)"
            />
            <div class="cam-link-radio-body">
              <span class="cam-link-radio-title">
                {{ 'modals.create_assessment.link_option_linked_title' | transloco }}
              </span>
              <span class="cam-link-radio-desc">
                {{ 'modals.create_assessment.link_option_linked_desc' | transloco }}
              </span>
            </div>
          </label>
          <label class="cam-link-radio" [class.selected]="!isLinkedToPa()">
            <input
              type="radio"
              name="cam-link-mode"
              [checked]="!isLinkedToPa()"
              (change)="onLinkModeChange(false)"
            />
            <div class="cam-link-radio-body">
              <span class="cam-link-radio-title">
                {{ 'modals.create_assessment.link_option_standalone_title' | transloco }}
              </span>
              <span class="cam-link-radio-desc">
                {{ 'modals.create_assessment.link_option_standalone_desc' | transloco }}
              </span>
            </div>
          </label>
        </div>

        @if (isLinkedToPa() && !isLoadingActivities() && !activityLoadFailed()) {
          @if (availableActivities().length === 0) {
            <p class="modal-intro muted">
              {{ 'modals.create_assessment.link_empty' | transloco }}
            </p>
          } @else if (activitySelectOptions().length > 0) {
            <div class="field cam-link-field">
              <label for="cam-link-activity">
                {{ 'modals.create_assessment.link_select_label' | transloco }}
              </label>
              <!-- Canonical `<app-select variant="form">` — native `<select>`
                   was banned May 2026 (OS-themed panels break dark-mode
                   modals + inconsistent typography). `variant="form"`
                   matches the field height and radius the rest of this
                   modal uses. `[clearable]="false"` because Link mode
                   requires a picked PA; an explicit clear would put the
                   Next button back into the disabled state without the
                   user meaning to. -->
              <app-select
                variant="form"
                inputId="cam-link-activity"
                [placeholder]="'modals.create_assessment.link_select_placeholder' | transloco"
                [options]="activitySelectOptions()"
                [clearable]="false"
                [value]="selectedActivityId()"
                (valueChange)="onActivityPicked($any($event) ?? null)"
              />
            </div>
          }
        }
        <!-- VER-380 — warn before submit: this vendor + country +
             activity already has an open TIA (or a recorded derogation). -->
        @if (duplicateOpenTia()) {
          <div class="warn-banner cam-duplicate-tia" role="alert">
            <span>{{
              (duplicateOpenTiaIsRecordOnly()
                ? 'tia.wizard.duplicate_recorded_derogation'
                : 'tia.wizard.duplicate_open_tia'
              ) | transloco: { subject: transferSubject() }
            }}</span>
            @if (duplicateOpenTiaUrl(); as url) {
              <a class="cam-duplicate-tia__link" [href]="url" target="_blank" rel="noopener">
                {{
                  (duplicateOpenTiaIsRecordOnly()
                    ? 'tia.wizard.view_existing_record'
                    : 'tia.wizard.view_existing_assessment'
                  ) | transloco
                }}
              </a>
            }
          </div>
        }
      }

      <!-- Step: Language.
           Locks the content language for the assessment BEFORE the
           details step so AI-generated fields honour the choice and
           the details form (assessment name auto-fill from PA name)
           renders in the right voice. Extracted from an inline
           segmented-control that used to live inside Details. -->
      @if (currentStep() === 'language') {
        <div class="step-body">
          <p class="step-hint">{{ 'language_step.hint_assessment' | transloco }}</p>
          <app-language-picker [(language)]="assessmentLanguage" appearance="cards" />
          <!-- Subtle mismatch note. Rendered ONLY when the user has
               explicitly picked a language that differs from the
               linked PA — the default flow auto-syncs so most users
               never see this. Non-blocking (they can still submit);
               the hint just discloses that the resulting DPIA will
               contain mixed-language content. Reuses `.step-hint`
               styling and pre-uppercased labels from the view-model
               so no bespoke chrome or pipe imports are needed. -->
          @if (languageMismatchDetail(); as m) {
            <p class="step-hint">
              {{
                'language_step.pa_mismatch_hint'
                  | transloco
                    : {
                        paName: m.paName,
                        paLanguage: m.paLanguage,
                        chosenLanguage: m.chosenLanguage,
                      }
              }}
            </p>
          }
        </div>
      }

      <!-- Step: Details -->
      @if (currentStep() === 'details') {
        <p class="modal-intro">
          {{ 'modals.create_assessment.intro' | transloco }}
        </p>

        @if (isStandardOrg()) {
          <div class="organization-section">
            <span class="cam-section-label">{{
              'modals.create_assessment.who_for_heading' | transloco
            }}</span>
            <div class="segmented-control">
              <button
                type="button"
                class="segment"
                [class.segment--active]="isForMyself()"
                (click)="onSegmentClick(true)"
              >
                {{ 'modals.create_assessment.for_myself' | transloco }}
              </button>
              <button
                type="button"
                class="segment"
                [class.segment--active]="!isForMyself()"
                (click)="onSegmentClick(false)"
              >
                {{ 'modals.create_assessment.for_customer' | transloco }}
              </button>
            </div>

            @if (!isForMyself()) {
              <div class="field">
                <label for="ca-customer-org">
                  {{ 'modals.create_assessment.customer_org_label' | transloco }}
                  <span class="required-mark" aria-hidden="true">*</span>
                </label>
                <input
                  id="ca-customer-org"
                  type="text"
                  [placeholder]="'modals.create_assessment.customer_org_placeholder' | transloco"
                  required
                  maxlength="200"
                  [value]="customerOrgName()"
                  (input)="customerOrgName.set($any($event.target).value)"
                />
                <small>{{ 'modals.create_assessment.customer_org_helper' | transloco }}</small>
              </div>
            }
          </div>
        }

        <div class="field">
          <label for="ca-name">
            {{ 'modals.create_assessment.assessment_name_label' | transloco }}
            <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <input
            id="ca-name"
            type="text"
            [placeholder]="'modals.create_assessment.assessment_name_placeholder' | transloco"
            required
            maxlength="100"
            [(ngModel)]="assessmentName"
          />
          <small>{{ 'modals.create_assessment.assessment_name_helper' | transloco }}</small>
        </div>

        <!-- Language is now a dedicated step earlier in the wizard.
             Surface the picked value here as a read-only pill so the
             user can confirm what will be authored in without opening
             a second control that would race the language-step choice. -->
        <div class="field">
          <span class="field-label">{{
            'modals.create_assessment.language_label' | transloco
          }}</span>
          <app-language-badge [language]="assessmentLanguage()" appearance="pill" />
        </div>

        <!-- Labels — canonical `.field` + `.chip-list` (forms.css) above
             a plain autocomplete input. Avoids `mat-chip-input`, which
             stringifies autocomplete option objects as `[object Object]`. -->
        <div class="field">
          <label for="ca-labels" [class.error-label]="labelExists()">
            @if (labelExists()) {
              {{ 'modals.create_assessment.label_exists' | transloco }}
            } @else {
              {{ 'modals.create_assessment.select_or_create_labels' | transloco }}
            }
          </label>

          <div class="chip-input" [class.error]="labelExists()">
            @if (selectedLabels().length > 0) {
              <ul
                class="chip-list"
                [attr.aria-label]="'modals.create_assessment.label_selection_aria' | transloco"
              >
                @for (label of selectedLabels(); track label.label_id) {
                  <li class="chip">
                    <span>{{ label.label_name }}</span>
                    <button
                      type="button"
                      class="chip-remove"
                      [attr.aria-label]="'modals.create_assessment.remove_label_aria' | transloco"
                      (click)="removeLabel(label)"
                    >
                      ×
                    </button>
                  </li>
                }
              </ul>
            }
            <input
              #labelInputEl
              id="ca-labels"
              [placeholder]="
                (selectedLabels().length
                  ? 'modals.create_assessment.add_another_label'
                  : 'modals.create_assessment.type_to_search'
                ) | transloco
              "
              [value]="labelInput()"
              (input)="onLabelInputChange($event)"
              (keydown)="onLabelInputKeydown($event)"
              [matAutocomplete]="auto"
            />
            @if (isCheckingLabel()) {
              <mat-icon class="chip-input-spinner" aria-hidden="true">progress_activity</mat-icon>
            }
          </div>

          <mat-autocomplete
            #auto="matAutocomplete"
            class="assessment-label-menu"
            (opened)="onAutocompleteOpened()"
            (closed)="onAutocompleteClosed()"
            (optionSelected)="onAutocompleteLabelSelected($event.option.value)"
          >
            @for (label of filteredLabels(); track label.label_id) {
              <mat-option [value]="label.label_id">
                {{ label.label_name }}
              </mat-option>
            }
            @if (hasMoreLabels()) {
              <mat-option
                class="assessment-label-load-more"
                [value]="loadMoreLabelValue"
                [disabled]="isLoadingLabels()"
              >
                <div class="assessment-label-load-more-content">
                  @if (isLoadingLabels()) {
                    <mat-icon class="spinner-icon" aria-hidden="true">progress_activity</mat-icon>
                    <span class="assessment-label-load-more-text">{{
                      'modals.create_assessment.loading_short' | transloco
                    }}</span>
                  } @else {
                    <span class="assessment-label-load-more-text">{{
                      'modals.create_assessment.load_more' | transloco
                    }}</span>
                  }
                </div>
              </mat-option>
            }
          </mat-autocomplete>

          @if (isCheckingLabel()) {
            <small>{{ 'modals.create_assessment.checking_label_exists' | transloco }}</small>
          } @else if (labelLimitHint()) {
            <small class="error-hint">{{ labelLimitHint() }}</small>
          } @else if (!labelExists()) {
            <small>{{ 'modals.create_assessment.labels_helper' | transloco }}</small>
          }
        </div>
      }

      <!-- Step 2: Context Documents — shared `.panel-picker-*` chrome
           (import-assistant / decisions-tab / doc-creation). Search +
           select-all + hard-capped scroll list; check glyph axes live in
           `styles/picker.css`. -->
      @if (currentStep() === 'context-docs') {
        <div class="cam-context-step">
          <div class="cam-context-step-header">
            <p class="modal-intro">
              {{ 'modals.create_assessment.context_docs_intro' | transloco }}
            </p>
            <app-button
              type="primary"
              variant="outline"
              size="small"
              (clicked)="showUploadModal.set(true)"
            >
              <mat-icon>upload_file</mat-icon>
              {{ 'modals.create_assessment.upload_new_doc' | transloco }}
            </app-button>
          </div>

          @if (isLoadingContextDocs()) {
            <div
              class="panel-picker-card"
              role="status"
              aria-live="polite"
              aria-busy="true"
              [attr.aria-label]="'modals.create_assessment.context_docs_intro' | transloco"
            >
              <div class="panel-picker-list panel-picker-list--modal">
                @for (i of [1, 2, 3]; track i) {
                  <div class="panel-picker-item" aria-hidden="true">
                    <div appSkeleton class="cam-context-skel-check"></div>
                    <div appSkeleton="circle" class="cam-context-skel-icon"></div>
                    <div class="panel-picker-info">
                      <div
                        appSkeleton
                        class="cam-context-skel-line cam-context-skel-line--name"
                      ></div>
                      <div
                        appSkeleton
                        class="cam-context-skel-line cam-context-skel-line--meta"
                      ></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          } @else if (contextDocsLoadError(); as loadError) {
            <app-access-banner
              [fullWidth]="true"
              [notice]="{ message: loadError, icon: 'warning', tone: 'warning' }"
            >
              <app-button type="secondary" size="small" (clicked)="loadContextDocuments()">
                {{ 'common.retry' | transloco }}
              </app-button>
            </app-access-banner>
          } @else if (contextDocuments().length === 0) {
            <p class="panel-picker-empty">
              {{ 'modals.create_assessment.no_context_docs' | transloco }}
            </p>
          } @else {
            <div class="panel-picker-card">
              <div class="panel-picker-search">
                <mat-icon>search</mat-icon>
                <input
                  type="text"
                  [placeholder]="'modals.doc_creation.search_documents_placeholder' | transloco"
                  [attr.aria-label]="'modals.doc_creation.search_documents_aria' | transloco"
                  [ngModel]="contextDocSearch()"
                  (ngModelChange)="contextDocSearch.set($event)"
                />
              </div>

              <div class="panel-picker-select-all">
                <button
                  type="button"
                  class="panel-picker-select-all-btn"
                  [class.selected]="contextDocsSelectionState() === 'all'"
                  [class.indeterminate]="contextDocsSelectionState() === 'some'"
                  [disabled]="contextDocRows().length === 0"
                  role="checkbox"
                  [attr.aria-checked]="
                    contextDocsSelectionState() === 'all'
                      ? 'true'
                      : contextDocsSelectionState() === 'some'
                        ? 'mixed'
                        : 'false'
                  "
                  (click)="toggleAllContextDocs()"
                >
                  <span class="panel-picker-check">
                    <mat-icon>{{
                      contextDocsSelectionState() === 'some' ? 'remove' : 'check'
                    }}</mat-icon>
                  </span>
                  <span class="panel-picker-select-all-label">
                    {{ 'modals.doc_creation.select_all' | transloco }}
                  </span>
                </button>
                <span class="panel-picker-selection-count">
                  {{
                    'modals.doc_creation.selected_of_total'
                      | transloco
                        : {
                            selected: selectedContextDocIds().size,
                            total: contextDocRows().length,
                          }
                  }}
                </span>
              </div>

              @if (contextDocSelectionLimitReached()) {
                <p class="panel-picker-empty" role="alert">
                  {{ 'modals.create_assessment.context_doc_selection_limit' | transloco }}
                </p>
              }

              @if (contextDocRows().length === 0) {
                <p class="panel-picker-empty">
                  {{ 'modals.doc_creation.no_docs_match_search' | transloco }}
                </p>
              } @else {
                <div class="panel-picker-list panel-picker-list--modal">
                  @for (row of contextDocRows(); track row.context_document_id) {
                    <div class="panel-picker-row">
                      <button
                        type="button"
                        class="panel-picker-item"
                        [class.selected]="row.selected"
                        [attr.aria-pressed]="row.selected"
                        (click)="toggleContextDoc(row.context_document_id)"
                      >
                        <span class="panel-picker-check">
                          <mat-icon>check</mat-icon>
                        </span>
                        <app-type-icon [icon]="row.icon" [variant]="row.iconVariant" size="small" />
                        <span class="panel-picker-info">
                          <span class="panel-picker-name">{{ row.file_name }}</span>
                          <span class="panel-picker-sub">
                            {{ row.typeLabel }}
                            @if (row.sizeLabel) {
                              · {{ row.sizeLabel }}
                            }
                            @if (row.uploaded_by_name) {
                              · {{ row.uploaded_by_name }}
                            }
                            @if (row.extraction; as extraction) {
                              ·
                              <app-status-indicator
                                appearance="inline"
                                [status]="extraction.variant"
                                [label]="extraction.labelKey | transloco"
                                [icon]="extraction.icon"
                                [meaning]="extraction.meaning"
                              />
                            }
                          </span>
                        </span>
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>

        @if (showUploadModal()) {
          <app-context-document-upload-modal
            [isVisible]="true"
            [orgId]="uploadCallerOrgId()"
            [customerOrgId]="uploadCustomerOrgId()"
            [ownership]="{ usage_scope: 'session', owner_id: draftSourceOwnerId() }"
            (closed)="onUploadModalClosed($event)"
            (cancelled)="onUploadModalCancelled()"
          />
        }
      }

      <!-- Step: Derogations (VER-331 step 6) — vendor-shaped types only
           (TIA today). Narrow, advisory-only check — never blocks the
           flow. The standing disclaimer is shown above the options at
           all times, per the ticket. -->
      @if (currentStep() === 'derogations') {
        <p class="modal-intro">{{ 'tia.wizard.derogations_intro' | transloco }}</p>
        <!-- VER-382 — important-notice icon + bold "narrow exceptions". -->
        <div class="cam-derogation-disclaimer" role="note">
          <mat-icon class="cam-derogation-disclaimer__icon" aria-hidden="true">error</mat-icon>
          <p>
            {{ 'tia.derogations.disclaimer_before' | transloco
            }}<strong>{{ 'tia.derogations.disclaimer_emphasis' | transloco }}</strong
            >{{ 'tia.derogations.disclaimer_after' | transloco }}
          </p>
        </div>

        <!-- VER-382 — multi-select; "None of these apply" is mutually
             exclusive with the derogations (each clears the other). Same
             checkbox-row pattern as the Context step. -->
        <ul
          class="context-docs-list cam-derogation-list"
          role="group"
          [attr.aria-label]="'tia.wizard.step_derogations' | transloco"
        >
          @for (option of derogationOptions; track option.id) {
            <li
              class="context-doc-item"
              [class.context-doc-item--selected]="selectedDerogationIds().has(option.id)"
              role="checkbox"
              tabindex="0"
              [attr.aria-checked]="selectedDerogationIds().has(option.id)"
              (click)="toggleDerogation(option.id)"
              (keydown.enter)="toggleDerogation(option.id)"
              (keydown.space)="$event.preventDefault(); toggleDerogation(option.id)"
            >
              <mat-checkbox
                color="primary"
                class="ctx-row-check"
                [checked]="selectedDerogationIds().has(option.id)"
                [disableRipple]="true"
                tabindex="-1"
                aria-hidden="true"
                (click)="$event.preventDefault()"
              />
              <div class="context-doc-info">
                <span class="context-doc-name">{{ option.labelKey | transloco }}</span>
                <span class="context-doc-desc">{{ option.descriptionKey | transloco }}</span>
              </div>
            </li>
          }
          <li
            class="context-doc-item cam-derogation-none"
            [class.context-doc-item--selected]="noneApply()"
            role="checkbox"
            tabindex="0"
            [attr.aria-checked]="noneApply()"
            (click)="toggleNoneApply()"
            (keydown.enter)="toggleNoneApply()"
            (keydown.space)="$event.preventDefault(); toggleNoneApply()"
          >
            <mat-checkbox
              color="primary"
              class="ctx-row-check"
              [checked]="noneApply()"
              [disableRipple]="true"
              tabindex="-1"
              aria-hidden="true"
              (click)="$event.preventDefault()"
            />
            <div class="context-doc-info">
              <span class="context-doc-name">{{ 'tia.derogations.none_apply' | transloco }}</span>
            </div>
          </li>
        </ul>

        @if (hasDerogationClaim()) {
          <div class="field cam-derogation-rationale">
            <label for="cam-derogation-rationale">
              {{ 'tia.derogations.rationale_label' | transloco }}
              <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <textarea
              id="cam-derogation-rationale"
              rows="3"
              maxlength="1000"
              [value]="derogationRationale()"
              (input)="updateDerogationRationale($any($event.target).value)"
            ></textarea>
          </div>
          <!-- VER-383 — the footer offers Record derogation / Continue with TIA. -->
          @if (canRecordOnly()) {
            <p class="tia-wizard-note">{{ 'tia.derogations.outcome_intro' | transloco }}</p>
          }
        }
      }

      <!-- Step 3: Review -->
      @if (currentStep() === 'processing-description') {
        <p class="modal-intro">
          {{ 'modals.create_assessment.processing_description_intro' | transloco }}
        </p>
        @if (isWaitingForSuggestion()) {
          <app-ai-generating-status appearance="banner">{{
            'modals.create_assessment.processing_description_loading' | transloco
          }}</app-ai-generating-status>
        } @else if (suggestionFailed()) {
          <div class="cam-pq-fallback" role="status">
            <p>{{ 'modals.create_assessment.processing_description_failed' | transloco }}</p>
            <app-button
              type="secondary"
              variant="ghost"
              size="small"
              (clicked)="retryDescriptionSuggestion()"
              >{{ 'common.retry' | transloco }}</app-button
            >
          </div>
        }
        @for (row of preQuestionnaireRows(); track row.id) {
          <div class="field cam-pq-field">
            <div class="cam-pq-label-row">
              <label [for]="'cam-pq-' + row.id">{{ row.question }}</label>
              @if (row.suggestedValue && !row.dismissed) {
                <app-field-suggestion
                  [label]="row.question"
                  [value]="row.suggestedValue"
                  [multiline]="true"
                  [rationale]="row.reasoning ?? ''"
                  [provenance]="descriptionSuggestion.result()?.context_sources?.join(', ') ?? ''"
                  [applied]="row.applied"
                  [quiet]="row.quiet"
                  [canUndo]="row.canUndo"
                  (accepted)="applyPreQuestionnaireSuggestion(row.id)"
                  (undone)="undoPreQuestionnaireSuggestion(row.id)"
                  (dismissed)="dismissPreQuestionnaireSuggestion(row.id)"
                />
                @if (row.isTentative && !row.applied) {
                  <small class="cam-pq-help">{{
                    'questionnaire.recommendation_tentative_note' | transloco
                  }}</small>
                }
              }
            </div>
            @if (row.helpText) {
              <small class="cam-pq-help" [id]="'cam-pq-help-' + row.id">{{ row.helpText }}</small>
            }
            <textarea
              [id]="'cam-pq-' + row.id"
              class="cam-pq-textarea"
              rows="8"
              maxlength="4000"
              [attr.aria-describedby]="row.helpText ? 'cam-pq-help-' + row.id : null"
              [required]="row.required"
              [value]="row.answer"
              [placeholder]="
                'modals.create_assessment.processing_description_placeholder' | transloco
              "
              (input)="onPreQuestionnaireAnswerChange(row.id, $any($event.target).value)"
            ></textarea>
          </div>
        }
        @if (preQuestionnaireRows().length === 0) {
          @if (isLoadingPreQuestionnaire()) {
            <div appSkeleton class="cam-pq-skeleton"></div>
          } @else {
            <div class="cam-pq-fallback" role="status">
              <p>{{ 'modals.create_assessment.processing_description_unavailable' | transloco }}</p>
              <app-button
                type="secondary"
                variant="ghost"
                size="small"
                (clicked)="loadPreQuestionnaireDefinition()"
                >{{ 'common.retry' | transloco }}</app-button
              >
            </div>
          }
        }
      }
      @if (currentStep() === 'review') {
        <!-- VER-380 — warn before submit: this vendor + country +
             activity already has an open TIA (or a recorded derogation). -->
        @if (duplicateOpenTia()) {
          <div class="warn-banner cam-duplicate-tia" role="alert">
            <span>{{
              (duplicateOpenTiaIsRecordOnly()
                ? 'tia.wizard.duplicate_recorded_derogation'
                : 'tia.wizard.duplicate_open_tia'
              ) | transloco: { subject: transferSubject() }
            }}</span>
            @if (duplicateOpenTiaUrl(); as url) {
              <a class="cam-duplicate-tia__link" [href]="url" target="_blank" rel="noopener">
                {{
                  (duplicateOpenTiaIsRecordOnly()
                    ? 'tia.wizard.view_existing_record'
                    : 'tia.wizard.view_existing_assessment'
                  ) | transloco
                }}
              </a>
            }
          </div>
        }
        <div class="review-summary">
          <div class="cam-review-row">
            <span class="review-label">{{
              'modals.create_assessment.review_organization' | transloco
            }}</span>
            <span class="review-value review-value--with-logo">
              <app-org-logo
                [logoUrl]="isForMyself() ? orgLogoUrl() : null"
                [name]="displayOrgName()"
                size="md"
              />
              {{ displayOrgName() }}
            </span>
          </div>

          @if (showVendorStep()) {
            <div class="cam-review-row">
              <span class="review-label">{{ 'tia.wizard.field_vendor' | transloco }}</span>
              <span class="review-value">{{ selectedVendor()?.global_vendor?.name }}</span>
            </div>
            <div class="cam-review-row">
              <span class="review-label">{{
                'modals.create_assessment.step_link' | transloco
              }}</span>
              <span class="review-value">
                {{ linkedActivityName() ?? ('tia.wizard.pa_not_linked' | transloco) }}
              </span>
            </div>
            @if (assessAnyway()) {
              <div class="cam-review-row">
                <span class="review-label">{{ 'tia.wizard.review_outcome' | transloco }}</span>
                <span class="review-value">{{
                  'tia.wizard.review_outcome_assess_anyway' | transloco
                }}</span>
              </div>
            } @else {
              <div class="cam-review-row">
                <span class="review-label">{{ 'tia.wizard.step_derogations' | transloco }}</span>
                <span class="review-value">
                  @if (noneApply()) {
                    {{ 'tia.derogations.none_apply_full' | transloco }}
                  } @else {
                    @for (id of selectedDerogationList(); track id; let last = $last) {
                      {{ 'tia.derogations.option_' + id + '_label' | transloco
                      }}{{ last ? '' : ', ' }}
                    }
                    — {{ derogationRationale() }}
                  }
                </span>
              </div>
              @if (hasDerogationClaim()) {
                <div class="cam-review-row">
                  <span class="review-label">{{ 'tia.wizard.review_outcome' | transloco }}</span>
                  <span class="review-value">{{
                    (isRecordOnly()
                      ? 'tia.derogations.outcome_record_label'
                      : 'tia.derogations.outcome_full_label'
                    ) | transloco
                  }}</span>
                </div>
              }
            }
          }

          @if (isStandardOrg()) {
            <div class="cam-review-row">
              <span class="review-label">{{
                'modals.create_assessment.review_assessment_for' | transloco
              }}</span>
              <span class="review-value">{{
                (isForMyself()
                  ? 'modals.create_assessment.review_myself'
                  : 'modals.create_assessment.review_customer'
                ) | transloco
              }}</span>
            </div>
          }

          <div class="cam-review-row">
            <span class="review-label">{{
              'modals.create_assessment.review_assessment_name' | transloco
            }}</span>
            <span class="review-value">{{ assessmentName() }}</span>
          </div>

          <div class="cam-review-row">
            <span class="review-label">{{
              'modals.create_assessment.review_language' | transloco
            }}</span>
            <span class="review-value">
              <app-language-badge [language]="assessmentLanguage()" appearance="pill" />
              <!-- Same mismatch hint as the language step — surfaced
                   here too so a user who jumped straight to Review
                   via the timeline still sees it before submitting. -->
              @if (languageMismatchDetail(); as m) {
                <span class="review-value-note">
                  {{
                    'language_step.pa_mismatch_hint'
                      | transloco
                        : {
                            paName: m.paName,
                            paLanguage: m.paLanguage,
                            chosenLanguage: m.chosenLanguage,
                          }
                  }}
                </span>
              }
            </span>
          </div>

          @if (selectedLabels().length > 0) {
            <div class="cam-review-row">
              <span class="review-label">{{
                'modals.create_assessment.review_labels' | transloco
              }}</span>
              <div class="review-labels">
                @for (label of selectedLabels(); track label.label_id) {
                  <span class="review-chip">{{ label.label_name }}</span>
                }
              </div>
            </div>
          }

          @if (selectedContextDocIds().size > 0) {
            <div class="cam-review-row">
              <span class="review-label">{{
                'modals.create_assessment.review_context_docs' | transloco
              }}</span>
              <span class="review-value"
                >{{ selectedContextDocIds().size }}
                {{ 'modals.create_assessment.docs_selected' | transloco }}</span
              >
            </div>
          }
        </div>

        <!-- Extraction gate: an attached document that is still being
             extracted contributes no text to the AI's grounding context,
             so creating now would produce the "no suggestion" outcome
             despite something being attached. -->
        @if (showPreQuestionnaireStep()) {
          @for (row of preQuestionnaireRows(); track row.id) {
            <section class="cam-description-review" [attr.aria-label]="row.question">
              <h3>{{ row.question }}</h3>
              <p>{{ row.answer }}</p>
              <app-button type="secondary" variant="ghost" size="small" (clicked)="onSecondary()">{{
                'modals.create_assessment.edit_description' | transloco
              }}</app-button>
            </section>
          }
        }
      }

      @if (
        (currentStep() === 'context-docs' || currentStep() === 'review') && hasExtractingSelection()
      ) {
        <div class="cam-extracting-note">
          <mat-icon class="cam-extracting-icon">hourglass_top</mat-icon>
          <span>
            {{
              'modals.create_assessment.extracting_wait'
                | transloco: { count: extractingSelection().length }
            }}
          </span>
        </div>
      }
    </div>
  }
</app-modal>
